/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {Box, Button, Stack, Text} from '@salesforce/retail-react-app/app/components/shared/ui'

/**
 * PageActionPlaceHolder is a React functional component that renders a placeholder
 * for page actions with a customizable heading, text, icon, and button.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.heading - The heading text to be displayed in uppercase.
 * @param {string} props.text - The descriptive text to be displayed below the heading.
 * @param {ReactNode} props.icon - The icon to be displayed above the heading.
 * @param {string} props.buttonText - The text to be displayed on the button.
 * @param {Object} props.buttonProps - Additional properties to be passed to the button component.
 * @param {function} props.onButtonClick - The callback function to be executed when the button is clicked.
 *
 * @returns {JSX.Element} A stack layout containing an icon, heading, text, and a button.
 */
const PageActionPlaceHolder = ({heading, text, icon, buttonText, buttonProps, onButtonClick}) => {
    return (
        <Stack spacing={2} py={12} px={4} alignItems="center">
            <Box>{icon}</Box>
            <Stack spacing={16} alignItems="center">
                <Box>
                    <Text
                        align="center"
                        variant="eyeBrowLarge"
                        textTransform={'uppercase'}
                        p={'16px 10px 8px'}
                    >
                        {heading}
                    </Text>
                    <Text align="center" variant={'bodyLarge2'} color="darkGray">
                        {text}
                    </Text>
                </Box>
                <Button onClick={onButtonClick} {...buttonProps} href={buttonProps.href}>
                    {buttonText}
                </Button>
            </Stack>
        </Stack>
    )
}

PageActionPlaceHolder.propTypes = {
    heading: PropTypes.string,
    text: PropTypes.string,
    buttonText: PropTypes.string,
    icon: PropTypes.any,
    buttonProps: PropTypes.object,
    onButtonClick: PropTypes.func
}

export default PageActionPlaceHolder
